import { withTransaction } from '@elastic/apm-rum-react'
import {
  Button,
  DakenService,
  FirebaseConnect,
  Input,
  InputPassword,
  JessicaJonesService,
  Loader,
  localForageHelper,
  LogService,
  ModalContract,
  MySquid,
  onLangChange,
  Provider,
  SheHulkService,
  UserAuth,
  UserHelper,
} from '@hub/shared'
import useDoLogin from '@src/hooks/use-do-login/use-do-login.hook'
import useMessage from '@src/hooks/use-message/use-message.hook'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import './login.page.scss'

const LoginPageComponent = ({ configs, setLoadingLoader }: { configs?: MySquid; setLoadingLoader?: (load: boolean) => void }) => {
  const logService = useMemo(() => new LogService(), [])
  const jessicaJonesService = useMemo(() => new JessicaJonesService(), [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const sheHulkService = useMemo(() => new SheHulkService(), [])
  logService.init('LoginPage')

  const { t, i18n } = useTranslation('loginPage')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [validEmail, setValidEmail] = useState(false)
  const [loadingFully, setLoadingFully] = useState(false)
  const [params] = useSearchParams()
  const [openModalContract, setOpenModalContract] = useState(false)
  const { doLogin, doLoginWithProvider, handlingErrors, resetLoginStorage, getUtms, loading } = useDoLogin({ email, password })
  const { showMessage } = useMessage()
  const { search } = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (setLoadingLoader && typeof setLoadingLoader === 'function') {
      setLoadingLoader(loadingFully)
    }
  }, [setLoadingLoader, loadingFully])

  const checkContractAccepted = useCallback(async () => {
    try {
      const { acceptedCurrent, acceptedCustom } = await sheHulkService.checkUserAcceptedContract('plataform', configs?._id ?? 'hub')
      const impersonateUser = await localForageHelper.getItem('impersonateUser')
      let accepted = false
      if (configs?.contract?.hasContract) {
        accepted = !!impersonateUser || acceptedCustom
      } else {
        accepted = !!impersonateUser || acceptedCurrent
      }
      setOpenModalContract(!accepted)
      return accepted
    } catch (error) {
      logService.error({
        message: 'Check Contract Accepted error',
        error,
      })
      return false
    }
  }, [configs, logService, sheHulkService])

  const redirectLogin = useCallback(async () => {
    let path = '/home'

    const followLink: string | null = await localForageHelper.getItem('follow_link')
    if (followLink?.length && followLink !== '/' && !followLink.includes('home')) {
      localForageHelper.removeItem('follow_link')
      path = followLink
    }

    const utms = await getUtms()

    const queryParams = new URLSearchParams()
    if (utms?.utm_source) queryParams.set('utm_source', utms.utm_source)
    if (utms?.utm_medium) queryParams.set('utm_medium', utms.utm_medium)
    if (utms?.utm_campaign) queryParams.set('utm_campaign', utms.utm_campaign)
    if (utms?.utm_term) queryParams.set('utm_term', utms.utm_term)
    if (utms?.utm_content) queryParams.set('utm_content', utms.utm_content)
    if (utms?.ad_id) queryParams.set('ad_id', utms.ad_id)
    if (utms?.ad_name) queryParams.set('ad_name', utms.ad_name)

    const finalUrl = queryParams.toString() ? `${path}?${queryParams.toString()}` : path
    history.pushState(null, document.title, finalUrl)
  }, [getUtms])

  const doContractAccepted = useCallback(async () => {
    try {
      setOpenModalContract(false)
      const response = await sheHulkService.postAcceptedContract('plataform', configs?._id ?? 'hub')
      if (response?.error) {
        throw new Error(response)
      }
      await redirectLogin()
    } catch (error) {
      logService.error({
        message: 'Fail to Contract Accepted error',
        error,
      })
      userHelper.deleteUser()
      showMessage({ type: 'error' })
      setLoadingFully(false)
    }
  }, [setOpenModalContract, configs, logService, showMessage, userHelper, redirectLogin, sheHulkService])

  const doContractRejected = useCallback(async () => {
    setOpenModalContract(false)
    userHelper.deleteUser()
    showMessage({ message: 'contract_refused', type: 'error' })
    setLoadingFully(false)
  }, [showMessage, userHelper, setOpenModalContract, setLoadingFully])

  const authenticate = useCallback(
    async ({ code, userAuth, network }: { code?: string; userAuth?: UserAuth; network: string }) => {
      if (!userAuth && code) {
        userAuth = await jessicaJonesService.generateJWT(code, network)
      }

      if (userAuth?.idToken) {
        const user = await dakenService.getInfluencersInfo(userAuth.idToken).catch((error) => {
          if (['unregistered_user', 'unregistered_user_mq'].includes(error?.error_description)) {
            return navigate(`/login/verify${search}`, { state: { email } })
          }

          throw error
        })

        const userInfo = {
          ...user,
          statusProviders: user.statusProviders,
          name: user.name,
        }

        if (configs?.hasControlAccess) {
          const thisEmail: string = userInfo?.profileadditionalinfo?.email ?? (await localForageHelper.getItem('email')) ?? ''
          if (configs?.controlAccess?.type === 'email' && !configs?.controlAccess?.users?.includes(thisEmail)) {
            throw {
              message: 'not_allowed',
            }
          }
          if (
            configs?.controlAccess?.type === 'document' &&
            !configs?.controlAccess?.users?.includes(user?.profileadditionalinfo?.document)
          ) {
            throw {
              message: 'not_allowed',
            }
          }
        }

        userHelper.setUserObject(userInfo, userAuth.idToken, userAuth.expiresIn)
        resetLoginStorage()

        const userLang = userInfo?.profileadditionalinfo?.language || i18n?.language || 'en'
        i18n.changeLanguage(userLang)
        onLangChange.next(userLang)
        const acceptedContract = await checkContractAccepted()
        if (acceptedContract) {
          await redirectLogin()
        } else {
          setOpenModalContract(true)
        }
      } else {
        setLoadingFully(false)
      }
    },
    [
      jessicaJonesService,
      checkContractAccepted,
      configs?.controlAccess?.type,
      configs?.controlAccess?.users,
      configs?.hasControlAccess,
      dakenService,
      i18n,
      redirectLogin,
      resetLoginStorage,
      userHelper,
      navigate,
      search,
      email,
    ]
  )

  const generateJWT = useCallback(
    async (codeOrToken: string, network: string) => {
      return await jessicaJonesService?.generateJWT(codeOrToken, network === 'youtube' ? 'google' : network).catch(async (error) => {
        if (['unregistered_user', 'unregistered_user_mq'].includes(error?.data?.error_description)) {
          const queryParams = new URLSearchParams(params)
          const utms = await getUtms()
          queryParams.set('utm_source', utms?.utm_source ?? '')
          queryParams.set('utm_medium', utms?.utm_medium ?? '')
          queryParams.set('utm_campaign', utms?.utm_campaign ?? '')
          queryParams.set('utm_content', utms?.utm_content ?? '')
          queryParams.set('ad_id', utms?.ad_id ?? '')
          queryParams.set('ad_name', utms?.ad_name ?? '')
          queryParams.set('network', network)
          queryParams.set('token', codeOrToken)
          navigate(`/login/verify?${queryParams.toString()}`)
        } else {
          logService.error({
            message: 'Error on google login',
            error,
          })
          throw error
        }
      })
    },
    [jessicaJonesService, logService, navigate, params, getUtms]
  )

  const continueLoginWithProvider = useCallback(
    async (network: string | null, codeOrToken: string | null) => {
      if (network && codeOrToken) {
        let userAuth: UserAuth | null = null
        switch (network) {
          case 'youtube':
            const jwtInfos = await generateJWT(codeOrToken, network)
            if (jwtInfos) {
              userAuth = jwtInfos
            }
            break
          default:
            break
        }

        if (userAuth) {
          await authenticate({ userAuth, network })
        }
      }
    },
    [authenticate, generateJWT]
  )

  const continueLogin = useCallback(async () => {
    try {
      setLoadingFully(true)
      const query = new URLSearchParams(window.location.search)
      const error = query.get('error')
      const code: string = query.get('code') ?? ''
      const hash = window?.location?.hash
      const token = query.get('token')
      const provider = query.get('network')

      if (token && provider) {
        await continueLoginWithProvider(provider, token)
      }

      let userAuth = null
      let impersonateAdmin = false
      let impersonateUser: any = null

      if (error) {
        history.pushState('', document?.title, window?.location?.pathname)
        throw new Error(error)
      }

      if (hash) {
        if (hash.includes('impersonate')) {
          const startHash = 'id_token='
          const endHash = '&scope='
          impersonateUser = hash?.includes('impersonate_user=') ? hash?.substring(hash?.lastIndexOf('impersonate_user=') + 17) : false
          impersonateAdmin = hash?.includes('admin=true')
          const token = hash?.substring(hash?.indexOf(startHash) + startHash?.length, hash?.indexOf(endHash))
          userAuth = await jessicaJonesService.generateImpersonate(token).catch((error) => {
            throw error
          })
        }
      }

      if (impersonateUser) {
        await localForageHelper.setItem('impersonateUser', {
          impersonateUser: decodeURI(impersonateUser) || false,
          impersonateAdmin: impersonateAdmin || false,
        })
      }

      await authenticate({ code, userAuth, network: 'firebase' })
    } catch (error: any) {
      const langStorage: string | null = await localForageHelper.getItem('language')
      if (langStorage) {
        i18n?.changeLanguage(langStorage || 'en')
      }
      userHelper.deleteUser()
      if (error?.data) {
        error = error.data
      }
      let customizedError = error?.error_description || error?.message || 'unexpected'
      handlingErrors(customizedError)
      logService.error({
        message: 'Login error',
        error,
      })
      setLoadingFully(false)
      history.pushState('', document?.title, window?.location?.origin)
    }
  }, [setLoadingFully, handlingErrors, authenticate, logService, userHelper, jessicaJonesService, i18n, continueLoginWithProvider])

  useEffect(() => {
    continueLogin()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (history.state?.usr?.toastMessage?.message) {
      if (history?.state?.usr?.toastMessage) {
        const { message, type = 'warning', duration } = history.state.usr.toastMessage
        showMessage({ message, type, duration })
        history.pushState({}, document?.title, window?.location?.pathname)
      }
    }
  }, [showMessage])

  return (
    <div>
      <h4>{loadingFully ? t('loadingLogin') : t('title')}</h4>
      {loadingFully ? (
        <Loader className="my-5" size="big" />
      ) : (
        <form onSubmit={doLogin} style={{ width: '100%' }}>
          <section className="social-login">
            <FirebaseConnect
              providers={['google']}
              onSelectedProvider={(providerSelected: Provider) => doLoginWithProvider(providerSelected)}
              loading={loading}
            />
          </section>
          <Input
            label={t('globalLogin:email')}
            type="email"
            placeholder="email@email.com"
            value={email}
            name="login-email"
            id="login-email"
            className="mb-3"
            onChange={(email: string) => setEmail(email.trim())}
            onValidate={(validEmail: boolean) => setValidEmail(validEmail)}
            readOnly={loading}
            loading={loading}
            autoComplete="email"
            required
          />
          <InputPassword
            label={
              <span className="display-flex" style={{ width: '100%' }}>
                {t('globalLogin:password')}{' '}
                <Link
                  className="forget-password"
                  state={{ prevPath: location.origin }}
                  to={{ pathname: '/login/forgot-password', search: params?.toString() }}
                >
                  {t('forgotPassword')}
                </Link>
              </span>
            }
            value={password}
            name="login-password"
            id="login-password"
            className="mb-3"
            placeholder="********"
            onChange={(password: string) => setPassword(password.trim())}
            readOnly={loading}
            loading={loading}
            autoComplete="current-password"
            required
          />
          <Button
            loading={loading}
            type="submit"
            disabled={(!email || !password || !validEmail) && !loading}
            className="button-login"
            size="xl"
            id="button-login"
          >
            {t('globalLogin:login')}
          </Button>
          <p className="mt-5">
            <span dangerouslySetInnerHTML={{ __html: t('register') }} />,{' '}
            <Link id="link-to-register" to={{ pathname: '/login/verify', search: params?.toString() }}>
              {t('registerLink')}
            </Link>
          </p>
        </form>
      )}
      <ModalContract
        onCloseChange={() => doContractRejected()}
        onAcceptedChange={() => doContractAccepted()}
        open={openModalContract}
        configId={configs?._id ?? 'hub'}
      />
    </div>
  )
}

export default withTransaction('LoginPageComponent', 'component')(LoginPageComponent)
